import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CandlestickChart,
  Clock,
  Copy,
  CopyPlus,
  FileChartLine,
  Layers,
  Pause,
  Play,
  Power,
  Settings2,
  Square,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ui/use-confirm";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  METRIC_TOOLTIP_TITLE_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  MINI_STAT_COUNT_GRADIENT,
  MINI_STAT_LOSS_GRADIENT,
  MINI_STAT_WIN_GRADIENT,
} from "@/utils/miniStatGradient";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import {
  deleteDeployment,
  deploymentAction,
  getDeployment,
  getDeploymentLive,
  loadLaunchpadCandles,
} from "../../../services/strategyDeployApi";
import type { StrategyRdOhlcBar } from "../../../types/strategyRd";
import type {
  DeployLiveMessage,
  DeploymentDetail as Detail,
  DeploymentLive,
  DeployPolicy,
  DeploySignal,
  Deployment,
} from "../../../types/strategyDeploy";
import { useStrategyDeployLive } from "../../../hooks/useStrategyDeployLive";
import { useOpenStrategyReport } from "../Workspace/useOpenStrategyReport";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";
import { ReportsMetaIconChip } from "../Reports/components/ReportsCardChrome";
import { SignalCard } from "./SignalCard";
import { LiveTradesTable } from "./LiveTradesTable";
import {
  BROKER_LABEL,
  MODE_LABEL,
  RED,
  fmtMoney,
  fmtPct,
  fmtR,
} from "./liveStrategiesFormat";
import { useAccountLabel } from "./useAccountLabel";
import { DeploymentSettingsPanel } from "./DeploymentSettingsDialog";
import { Heartbeat } from "./Heartbeat";
import { LiveLog } from "./LiveLog";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveCardTitleClassName,
  liveFigureClassName,
  liveLabelClassName,
  liveOptionTitleClassName,
  liveSectionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";
import { DeployModeBadge, DeploymentStatusBadge, LiveBadge, LiveIconTile } from "./LiveUiParts";
import { LiveChart } from "./LiveChart";
import { AccountLine, AccountStrip, ExpectationCurve, LimitsMeters, NextStep, UptimeStrip } from "./LivePanels";

type Props = {
  deploymentId: string | null;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
  onDecide: (signal: DeploySignal, decision: "approve" | "reject") => Promise<void>;
  /** Deploy this strategy again (another mode, account or version) to run side by side. */
  onRunAlongside?: (dep: Deployment) => void;
};

function signedGradient(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v === 0) return MINI_STAT_COUNT_GRADIENT;
  return v > 0 ? MINI_STAT_WIN_GRADIENT : MINI_STAT_LOSS_GRADIENT;
}

const ACTION_BUTTON_CLASS = "h-8 gap-1.5 px-3 shadow-none";

/** Which Strategies-page policy runs on top of the Lab rules, and what of it runs live. */
function PolicyNote({ policy }: { policy: DeployPolicy }) {
  const parts = [
    policy.entry_filters ? `${policy.entry_filters} entry filter${policy.entry_filters === 1 ? "" : "s"}` : null,
    policy.exit_overrides ? `${policy.exit_overrides} stop / target rule${policy.exit_overrides === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return (
    <div className={cn(liveCardClassName, "flex-row items-start gap-3 p-5")} data-live-policy="">
      <LiveIconTile icon={Layers} />
      <div className="flex min-w-0 flex-col gap-1">
        <span className={cn(liveOptionTitleClassName, "flex flex-wrap items-baseline gap-x-2")}>
          {policy.strategy_name}, policy v{policy.version_number ?? "?"}
          {policy.title ? <span className={liveSmallTextClassName}>{policy.title}</span> : null}
        </span>
        <span className={liveSmallTextClassName}>
          {policy.lab_rules_only
            ? "This version is the strategy's own Strategy Lab rules, with nothing extra on top. Later versions from your reports add filters and stop / target rules."
            : `Entries from its Strategy Lab rules${parts.length ? `, plus ${parts.join(" and ")}` : ""}.`}
        </span>
      </div>
    </div>
  );
}

/** What each control does, in the order of how far it goes. */
const CONTROL_HELP = {
  pause: "No new trades until you resume. Open trades keep their stop and target.",
  resume: "Starts looking for new trades again.",
  stop: "Ends the run: no new trades, and open trades are still managed until they close. You can resume it later or delete it.",
  kill: "Emergency stop: open positions are closed at market now and the strategy stops for good. It can't be resumed.",
} as const;

function ControlTip({ title, body, children }: { title: string; body: string; children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* A span trigger keeps the button's own slot (its sentence-case text) and shows the tip when disabled. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">{children}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className={metricTooltipShellClassName("max-w-64")} arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}>
          <p className={METRIC_TOOLTIP_TITLE_CLASS}>{title}</p>
          <p className={cn("mt-1 text-[11px] leading-snug", METRIC_TOOLTIP_BODY_CLASS)}>{body}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function DeploymentDetail({ deploymentId, onOpenChange, onChanged, onDecide, onRunAlongside }: Props): JSX.Element {
  const accountName = useAccountLabel();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The dialog's two panels: the detail, or settings slid in beside it. */
  const [view, setView] = useState<"detail" | "settings">("detail");
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { openReport: openStrategyReport } = useOpenStrategyReport();
  const [live, setLive] = useState<DeploymentLive | null>(null);
  const [fallbackCandles, setFallbackCandles] = useState<StrategyRdOhlcBar[]>([]);

  const load = useCallback(async () => {
    if (!deploymentId) return;
    try {
      const [d, l] = await Promise.all([getDeployment(deploymentId), getDeploymentLive(deploymentId).catch(() => null)]);
      setDetail(d);
      if (l) setLive(l);
    } catch (e) {
      presentMutationError(e, "Could not load this deployment");
    }
  }, [deploymentId]);

  useEffect(() => {
    setView("detail");
    setDetail(null);
    setLive(null);
    setFallbackCandles([]);
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  // Pine strategies (no scan) and brand-new deployments: plain candles until the worker's view exists.
  const depSymbol = detail?.deployment.symbol;
  const depTf = detail?.deployment.candle_type;
  const needFallback = !!detail && live != null && !live.view?.candles?.length;
  useEffect(() => {
    if (!needFallback || !depSymbol || !depTf) return;
    let alive = true;
    loadLaunchpadCandles(depSymbol, depTf, 3)
      .then((candles) => alive && setFallbackCandles(candles.slice(-180)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [needFallback, depSymbol, depTf]);

  useStrategyDeployLive(
    useCallback(
      (msg: DeployLiveMessage) => {
        if (msg.data?.deployment_id === deploymentId) void load();
      },
      [deploymentId, load]
    )
  );

  const dep = detail?.deployment ?? null;
  const signals = useMemo(() => detail?.signals ?? [], [detail]);
  const chartCandles = live?.view?.candles?.length ? live.view.candles : fallbackCandles;
  const closed = signals.filter((s) => s.status === "closed");
  const wins = closed.filter((s) => (s.r_multiple ?? s.pnl ?? 0) > 0).length;
  const sumR = closed.reduce((a, s) => a + (s.r_multiple ?? 0), 0);
  const pnl = closed.reduce((a, s) => a + (s.pnl ?? 0), 0);
  const open = signals.filter((s) => ["pending", "approved", "submitted", "open", "paper"].includes(s.status));

  /** The report is made on the strategy's Workspace canvas (under its node), with this run's
   *  trades preselected; without a strategy on a canvas, on Reports as before. */
  const openReport = async () => {
    if (!dep) return;
    setBusy("report");
    try {
      await openStrategyReport(dep.strategy_snapshot?.policy?.strategy_id ?? null, `paper:${dep.id}`);
    } finally {
      setBusy(null);
    }
  };

  const act = async (action: "pause" | "resume" | "stop" | "kill") => {
    if (!dep) return;
    if (
      action === "kill" &&
      !(await confirm({
        title: `Kill ${dep.name}?`,
        description: CONTROL_HELP.kill,
        confirmLabel: "Kill switch",
      }))
    )
      return;
    setBusy(action);
    try {
      await deploymentAction(dep.id, action);
      await load();
      onChanged();
    } catch (e) {
      presentMutationError(e, `Could not ${action} the deployment`);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!dep) return;
    if (
      !(await confirm({
        title: `Delete ${dep.name}?`,
        description: "Its signals and history are deleted too. This can't be undone.",
      }))
    )
      return;
    try {
      await deleteDeployment(dep.id);
      onChanged();
      onOpenChange(false);
    } catch (e) {
      presentMutationError(e, "Could not delete the deployment");
    }
  };

  const account = dep
    ? dep.broker_name
      ? `${BROKER_LABEL[dep.broker_name] ?? dep.broker_name} ${accountName(dep.broker_name, dep.broker_account_code)}`
      : "Paper account"
    : "";
  const source = dep
    ? dep.source === "pine"
      ? "TradingView"
      : dep.strategy_snapshot?.policy
        ? `Policy v${dep.strategy_snapshot.policy.version_number ?? "?"}`
        : "Strategy Lab"
    : "";
  const heroes = dep
    ? [
        {
          id: "trades",
          title: "Trades",
          amount: String(closed.length),
        },
        {
          id: "won",
          title: "Won",
          amount: closed.length ? fmtPct(wins / closed.length) : "N/A",
          gradient: MINI_STAT_COUNT_GRADIENT,
        },
        {
          id: "avg-r",
          title: "Avg R",
          amount: closed.length ? fmtR(sumR / closed.length) : "N/A",
          gradient: signedGradient(closed.length ? sumR : null),
        },
        { id: "pnl", title: "P&L", amount: fmtMoney(pnl), gradient: signedGradient(pnl) },
      ]
    : [];
  const panelClass = (active: boolean) =>
    cn(
      "h-full w-1/2 min-w-0 overflow-y-auto overflow-x-hidden p-6",
      // Hidden once slid away so its fields are not reachable by Tab.
      active ? "visible" : "invisible transition-[visibility] delay-300"
    );

  return (
    <>
      {confirmDialog}
      <Dialog open={!!deploymentId} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex h-[min(92vh,1100px)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1320px,calc(100vw-4rem))]"
          data-live-deployment-detail=""
          data-live-detail-view={view}
          data-live-ui=""
        >
        <div className="flex shrink-0 flex-col gap-3 px-6 pt-6 pb-4 pr-14 !border-0 !border-b !border-solid !border-border" data-live-detail-header="">
          <div className="flex flex-wrap items-center gap-3">
            <DialogTitle className={cn(liveSectionTitleClassName, "text-xl")}>{dep?.name ?? "Loading…"}</DialogTitle>
            {dep ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <DeploymentStatusBadge status={dep.status} />
                <DeployModeBadge mode={dep.mode} />
              </span>
            ) : null}
          </div>
          <DialogDescription className="sr-only">
            {dep ? `${dep.symbol}, ${dep.candle_type}, ${MODE_LABEL[dep.mode]}, ${account}` : "Loading the deployment"}
          </DialogDescription>
          {dep ? (
            <div className="flex flex-wrap items-center gap-1.5" data-live-detail-meta="">
              <ReportsMetaIconChip id="live-detail-symbol" label="Market" value={dep.symbol} icon={CandlestickChart} />
              <ReportsMetaIconChip id="live-detail-timeframe" label="Timeframe" value={dep.candle_type} icon={Clock} />
              <ReportsMetaIconChip id="live-detail-account" label="Account" value={account} icon={UserRound} />
              <ReportsMetaIconChip id="live-detail-source" label="Source" value={source} icon={Layers} />
              <Heartbeat dep={dep} className="ml-2 text-gray-500 dark:text-white" />
            </div>
          ) : null}
        </div>
          {/* Only the body scrolls, so its scrollbar starts below the close button. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <div
              className="flex h-full w-[200%] transition-transform duration-300 ease-out motion-reduce:transition-none"
              style={{ transform: view === "settings" ? "translateX(-50%)" : "translateX(0)" }}
            >
              {/* Panel 1: the deployment. */}
              <div className={panelClass(view === "detail")} aria-hidden={view !== "detail"} data-live-detail-panel="detail">
                <div className="flex min-w-0 flex-col gap-4">
                  {dep ? (
                    <>
                      <div className="flex flex-wrap gap-2" data-live-detail-actions="">
                        {dep.status !== "killed" ? (
                          <Button
                            size="sm"
                            variant="outline-brand"
                            data-font="primary"
                            className={ACTION_BUTTON_CLASS}
                            disabled={!!busy}
                            onClick={() => setView("settings")}
                            data-live-settings=""
                          >
                            <Settings2 className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Settings</span>
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline-brand"
                          data-font="primary"
                          className={ACTION_BUTTON_CLASS}
                          disabled={!!busy}
                          onClick={() => void openReport()}
                          title="Build a report from this strategy's closed trades, then save a new policy version and deploy it"
                          data-live-report=""
                        >
                          <FileChartLine className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Analyse in a report</span>
                        </Button>
                        {onRunAlongside && dep.source === "lab" ? (
                          <Button
                            size="sm"
                            variant="outline-brand"
                            data-font="primary"
                            className={ACTION_BUTTON_CLASS}
                            onClick={() => onRunAlongside(dep)}
                            title="Run it again on paper, live or another version, side by side"
                          >
                            <CopyPlus className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Run alongside</span>
                          </Button>
                        ) : null}
                        {dep.status === "stopped" || dep.status === "killed" || dep.status === "error" ? (
                          <Button size="sm" variant="outline-red" data-font="primary" className={ACTION_BUTTON_CLASS} onClick={remove}>
                            <Trash2 className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Delete</span>
                          </Button>
                        ) : null}
                        {/* Pause, Stop and Kill switch side by side, each saying what it does. */}
                        <div className="ml-auto flex flex-wrap gap-2" data-live-detail-controls="">
                          {dep.status === "running" ? (
                            <ControlTip title="Pause" body={CONTROL_HELP.pause}>
                              <Button size="sm" variant="outline-brand" data-font="primary" className={ACTION_BUTTON_CLASS} disabled={!!busy} onClick={() => act("pause")}>
                                <Pause className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Pause</span>
                              </Button>
                            </ControlTip>
                          ) : dep.status !== "killed" ? (
                            <ControlTip title="Resume" body={CONTROL_HELP.resume}>
                              <Button size="sm" variant="outline-brand" data-font="primary" className={ACTION_BUTTON_CLASS} disabled={!!busy} onClick={() => act("resume")}>
                                <Play className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Resume</span>
                              </Button>
                            </ControlTip>
                          ) : null}
                          {dep.status === "running" || dep.status === "paused" ? (
                            <ControlTip title="Stop" body={CONTROL_HELP.stop}>
                              <Button size="sm" variant="outline-brand" data-font="primary" className={ACTION_BUTTON_CLASS} disabled={!!busy} onClick={() => act("stop")} data-live-stop="">
                                <Square className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Stop</span>
                              </Button>
                            </ControlTip>
                          ) : null}
                          {dep.status !== "killed" ? (
                            <ControlTip title="Kill switch" body={CONTROL_HELP.kill}>
                              <Button size="sm" variant="gradient-red" data-font="primary" className={ACTION_BUTTON_CLASS} disabled={!!busy} onClick={() => act("kill")} data-live-kill="">
                                <Power className="size-3.5" aria-hidden /> <span className="normal-case tracking-normal">Kill switch</span>
                              </Button>
                            </ControlTip>
                          ) : null}
                        </div>
                      </div>

                      {dep.strategy_snapshot?.policy ? <PolicyNote policy={dep.strategy_snapshot.policy} /> : null}

                      {dep.last_error ? (
                        <div
                          className={cn(liveCardClassName, "p-5 !border-[color:var(--brand-glacial-red)] font-[family-name:var(--font-primary)] text-sm")}
                          style={{ color: RED }}
                        >
                          {dep.last_error}
                        </div>
                      ) : null}

                      {dep.source === "pine" && dep.webhook_url ? (
                        <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-webhook="">
                          <span className={liveOptionTitleClassName}>TradingView alert (webhook)</span>
                          <span className={liveSmallTextClassName}>
                            This strategy runs in TradingView, not here: each time it fires, TradingView sends us an alert and we
                            place the trade. In TradingView: Create alert, your strategy, Notifications, Webhook URL, then paste
                            the message below. TradingView fills in the action (buy / sell / close) and price; set sl / tp to your
                            stop and target, or leave them 0 to use the stop distance from the settings.
                          </span>
                          {[
                            ["Webhook URL", dep.webhook_url],
                            ["Message", dep.alert_template ?? ""],
                          ].map(([label, value]) => (
                            <div key={label} className="flex flex-col gap-1.5">
                              <span className={liveLabelClassName}>{label}</span>
                              <div className="flex items-center gap-2">
                                <code className="min-w-0 flex-1 truncate rounded-[6px] bg-muted px-3 py-2 text-xs text-gray-500 dark:text-white">{value}</code>
                                <Button
                                  size="icon-sm"
                                  variant="outline-brand"
                                  className="size-8 shadow-none"
                                  title={`Copy ${label}`}
                                  aria-label={`Copy ${label}`}
                                  onClick={() => {
                                    void navigator.clipboard.writeText(String(value));
                                    toast.success(`${label} copied`);
                                  }}
                                >
                                  <Copy className="size-3.5" aria-hidden />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      <Tabs data-trade-log-tabs="" defaultValue="overview">
                        <TabsList>
                          <TabsTrigger value="overview">Overview</TabsTrigger>
                          <TabsTrigger value="trades">Trades ({signals.length})</TabsTrigger>
                          <TabsTrigger value="activity">Live log</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="flex flex-col gap-4 pt-4">
                          {dep.mode !== "paper" ? (
                            <AccountStrip account={live?.account} dep={dep} />
                          ) : (
                            <AccountLine
                              title="Paper account"
                              cells={[
                                ["Equity", fmtMoney((dep.config?.paper?.equity ?? 10000) + pnl, false), undefined],
                                ["Started at", fmtMoney(dep.config?.paper?.equity ?? 10000, false), undefined],
                              ]}
                              note="No real money"
                              data-live-paper-account=""
                            />
                          )}
                          <NextStep next={live?.next ?? dep.next} />
                          <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-what-it-sees="">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className={liveCardTitleClassName}>What it sees</span>
                              <span className={liveSmallTextClassName}>
                                {dep.symbol}, {dep.candle_type}
                                {live?.view?.saved_at
                                  ? `, updated ${new Date(live.view.saved_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
                                  : ""}
                              </span>
                            </div>
                            <LiveChart dep={dep} candles={chartCandles} matches={live?.view?.matches ?? []} signals={signals} height={360} />
                          </div>
                          <UptimeStrip uptime={live?.uptime} />
                          <LimitsMeters limits={live?.limits} />
                          <div data-mini-stats-grid="" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                            {heroes.map((h) => (
                              <TradeLogMiniStatistics
                                key={h.id}
                                title={h.title}
                                amount={h.amount}
                                percentage={null}
                                showPercentage={false}
                                backgroundGradient={h.gradient}
                                fitAmount
                              />
                            ))}
                          </div>
                          <ExpectationCurve dep={dep} signals={signals} />
                          {open.length ? (
                            <div className="flex flex-col gap-4">
                              <span className={liveSectionTitleClassName}>Now</span>
                              <div className="grid gap-4 lg:grid-cols-2">
                                {open.map((s) => (
                                  <SignalCard key={s.id} signal={s} deployment={dep} onDecide={onDecide} />
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </TabsContent>

                        <TabsContent value="trades" className="pt-4">
                          <LiveTradesTable signals={signals} />
                        </TabsContent>

                        <TabsContent value="activity" className="pt-4">
                          <LiveLog deployments={[dep]} deploymentId={dep.id} initial={detail?.events ?? []} maxHeight={560} />
                        </TabsContent>
                      </Tabs>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Panel 2: settings, slid in beside the detail (no nested dialog). */}
              <div className={panelClass(view === "settings")} aria-hidden={view !== "settings"} data-live-detail-panel="settings">
                <div className="flex min-w-0 flex-col gap-4">
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-brand"
                      data-font="primary"
                      className={ACTION_BUTTON_CLASS}
                      onClick={() => setView("detail")}
                      data-live-settings-back=""
                    >
                      <ArrowLeft className="size-3.5" aria-hidden />
                      <span className="normal-case tracking-normal">Back</span>
                    </Button>
                  </div>
                  {dep ? (
                    <div className={cn(liveCardClassName, liveCardBodyClassName)}>
                      <DeploymentSettingsPanel
                        dep={dep}
                        active={view === "settings"}
                        onDone={() => setView("detail")}
                        onSaved={() => {
                          void load();
                          onChanged();
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter
            showCloseButton={false}
            className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 dark:border-border dark:bg-sidebar sm:flex-row sm:justify-end"
          >
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              className="h-8 shrink-0 px-5 text-[0.75rem]"
              onClick={() => onOpenChange(false)}
              data-live-detail-close=""
            >
              <span className="normal-case tracking-normal">Close</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
